import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';
import type { CustomPropertyDefinitionResponse, CustomPropertyReferenceEntityType } from '@zag/core/models';

export interface ReferenceOptionResponse {
  value: string;
  label: string;
  icon: string;
  description: string | null;
  color: string | null;
  imageUrl?: string | null;
  data?: Record<string, unknown> | null;
}

/**
 * Service for reading custom-property definitions and their reference options
 * (`/api/v1/core/custom-property-definitions/...`), plus a helper for
 * persisting a given entity's `customProperties` through its canonical REST
 * resource.
 *
 * The edit-section component stores custom-properties inside the entity's
 * own JSONB `data.customProperties` — the target resource path is dynamic
 * (determined per-entity by the edit facade), so the patch helper takes the
 * resource path as an argument rather than hard-coding a single URL.
 */
@Injectable({ providedIn: 'root' })
export class CustomPropertyDefinitionsService {
  private readonly http = inject(HttpClient);
  private readonly basePath = '/api/v1/core/custom-property-definitions';

  /** List all custom-property definitions for a given entity type. */
  list(entityType: string): Observable<CustomPropertyDefinitionResponse[]> {
    return this.http.get<CustomPropertyDefinitionResponse[]>(this.basePath, {
      params: { entityType },
    });
  }

  /**
   * Returns the distinct values that are actually stored for a given custom-property
   * key on the entities of `entityType`. Useful for pre-filtering filter-panel options
   * so only values with at least one matching record are shown.
   */
  listDistinctValues(entityType: string, propertyKey: string): Observable<string[]> {
    return this.http.get<string[]>(`${this.basePath}/distinct-values/list`, {
      params: { entityType, propertyKey },
    });
  }

  /**
   * Fetch the reference-options dataset for a definition, filtered by the
   * entity type that hosts the definition and the reference-entity kind
   * (`location`, `skill`, …).
   */
  listReferenceOptions(entityType: string, referenceEntityType: CustomPropertyReferenceEntityType): Observable<ReferenceOptionResponse[]> {
    return this.http.get<ReferenceOptionResponse[]>(`${this.basePath}/reference-options/list`, {
      params: { entityType, referenceEntityType },
    });
  }

  /**
   * Persist an entity's `customProperties` by PATCHing the entity's canonical
   * REST resource. Callers pass in the resource path (e.g.
   * `/api/v1/core/persons`) — the service does not assume a specific entity
   * type.
   */
  patchEntityCustomProperties(resourcePath: string, entityId: string, customProperties: Record<string, unknown>): Observable<unknown> {
    return this.http.patch(`${resourcePath}/${entityId}`, {
      data: { customProperties },
    });
  }
}
